/**
 * M² preset — STARTER TEMPLATE ONLY.
 *
 * Tenants do NOT consume this at runtime. Each tenant's full brand lives in
 * `tenant.tenant_settings.brand_overrides` (JSONB). This file is offered as
 * "Apply M² preset" inside the White-Label Editor — clicking the button copies
 * these values into the editor form, ready to save.
 *
 * Bottom line: editing this file changes the *starter template*, not any
 * existing tenant's brand.
 *
 * Colors and fonts extracted from BrandBook July 2023.
 *
 * Brand palette:
 *   Primary blue:  #6CC7EF  (sky/ocean blue - the signature M² color)
 *   Yellow/amber:  #FAB600  (warm accent - warnings, highlights)
 *   Lime green:    #C4D600  (fresh accent - success states)
 *   Red:           #D50032  (bold accent - errors, alerts)
 *
 * Fonts:
 *   Headings EN:   Futura (fallback: Jost — closest Google Font)
 *   Body EN:       Manrope (Google Fonts)
 *   Hebrew:        Ploni (fallback: Varela Round — Google Fonts)
 */

import type { BrandConfig } from '../types';

// Logo/icon are served from STABLE, unhashed public/ paths (NOT Vite imports).
// A Vite import produces a build-hashed URL (e.g. /assets/Logo large-ABC123.png)
// which, once saved into a tenant's brand_overrides, breaks on the next rebuild
// (new hash) or host change. A public/ path is verbatim + relative, so a stored
// value keeps working forever. Source files live in public/brand/m2/.
const logoLarge = '/brand/m2/logo-large.png';
const iconCoinBlue = '/brand/m2/icon-coin-blue.png';
const iconWhite = '/brand/m2/icon-white.png';
const iconPatternBlue = '/brand/m2/icon-pattern-blue.png';
// 10th-anniversary treatment (temporary) — remove alongside anniversaryIcon/
// anniversaryLogo in types.ts once the celebration period ends.
const logo10Years = '/brand/m2/logo-10years.png';
const icon10Years = '/brand/m2/icon-10years.png';

// Agent avatar templates — the 2026 brand-book patterns, four per M² palette
// colour, on the same stable public/ paths (frontend/public/brand/m2/agent-avatars/).
// The order (blue, green, red, yellow) is load-bearing: an agent whose colour has
// no set is hashed into this list, exactly as every agent was before avatars
// became a brand asset. Hex values repeat TEXTURE_COLOR_HEX (brand/textures.ts) —
// not imported, because this file is compiled alone into config/brand_presets.json.
const avatarSet = (color: string, family: string) => ({
  color,
  images: [1, 2, 3, 4].map(n => `/brand/m2/agent-avatars/${family}/${family}-${n}.svg`),
});


const m2: BrandConfig = {
  id: 'm2',
  name: 'M²',
  tagline: 'The future of Jewish life.',
  companyName: 'M²',
  companyUrl: 'https://www.ieje.org',
  pageTitle: 'M² - AI Platform',

  // Login uses the large M² wordmark; sidebar rail uses the small blue coin
  // mark (white on dark theme). No login-logo dark variant — `useBrandAssets`
  // falls back to `logo` when `logoDark` is unset.
  logo: logoLarge,
  icon: iconCoinBlue,
  iconDark: iconWhite,
  // Concierge front door gets the patterned "sunburst" coin instead of the
  // plain sidebar mark — it's the platform's meta-agent, so its icon reads
  // as a distinct, more decorative variant of the same brand mark.
  conciergeIcon: iconPatternBlue,
  // Anniversary swap — ONLY the sidebar mark and the login-page logo change;
  // every other `icon`/`logo` consumer (splash screen, concierge header,
  // chat avatars) is untouched.
  // Both marks are flat navy on transparent and no dark variant has been
  // drawn. The login panel is light in both themes (which is why there is no
  // `logoDark`), so the "10 Years" lockup serves both; the sidebar rail is
  // dark in dark mode (hence `iconDark`), so with `anniversaryIconDark` unset
  // the rail shows the regular white mark there — the celebration is absent
  // on the dark rail, never unreadable. Add `anniversaryIconDark` here the
  // day the designer supplies a light-on-dark "10" mark.
  anniversaryIcon: icon10Years,
  anniversaryLogo: logo10Years,

  colors: {
    primary: '#6CC7EF',
    primaryHover: '#4FB8E3',
    secondary: '#FAB600',
    secondaryLight: '#FFF4D6',
    secondaryDark: '#D49B00',
    accent: '#C4D600',
    gradientPrimary: 'linear-gradient(135deg, #6CC7EF 0%, #4FB8E3 50%, #3DA8D4 100%)',
  },

  // Dark mode: deep ocean-tinted darks — subtle M² blue undertone
  darkMode: {
    bgPrimary: '#0b1018',
    bgSecondary: '#111a24',
    bgTertiary: '#17222e',
    bgElevated: '#1c2935',
  },

  // Light mode: clean whites — no warm cream tint anywhere
  lightMode: {
    bgPrimary: '#ffffff',
    bgSecondary: '#ffffff',
    bgTertiary: '#f7f8fa',
    bgElevated: '#f1f3f6',
  },

  fonts: {
    heading: 'Futura',
    body: 'Manrope',
    mono: 'JetBrains Mono',
    // Jost is loaded from Google Fonts as the Futura fallback (Futura is a
    // proprietary font — most users don't have it installed). Jost is
    // geometrically near-identical and used everywhere Futura isn't available.
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700&family=Jost:wght@300;500;700&family=Varela+Round&family=JetBrains+Mono:wght@400;500&display=swap',
  },

  // Login page — eyebrow uses the product/sub-brand label "JewishPro" (the
  // platform's product name within M²), kept separate from `name` so the
  // sidebar alt text and chat input placeholder still read "M²". Tagline
  // uses `tagline`; welcome heading uses i18n.
  // Feature tick colors map to the M² brand palette (primary blue, secondary
  // amber, accent lime) — not the platform status colors — to match the
  // staging design's three-color check marks.
  loginEyebrow: 'JewishPro',
  // i18n key paths, exactly like default.ts — the login resolves them with
  // t(label, {defaultValue: label}), so a tenant that overrides these with its
  // own prose still renders that prose verbatim.
  loginFeatures: [
    { label: 'login.features.multiProvider', color: 'primary' },
    { label: 'login.features.enterpriseGrade', color: 'secondary' },
    { label: 'login.features.realtimeKnowledge', color: 'accent' },
  ],

  // M² brand-specific visual treatments. The semicircle is part of the
  // identity; color=null so it follows the selected agent's hue (same
  // behavior as PR #4). The chat tint is the M² spec — light/hue/deep
  // shades flow from the selected agent into the conversation surface.
  homeSemicircle: { enabled: true, color: null },
  chat: { tintByAgent: true },

  // Textured agent avatars — the M² look. Brands without sets draw circles.
  agentAvatars: {
    textures: [
      avatarSet('#6CC7EF', 'blue'),
      avatarSet('#C4D600', 'green'),
      avatarSet('#D50032', 'red'),
      avatarSet('#FAB600', 'yellow'),
    ],
  },

  // `terminology` is intentionally omitted: M² keeps its "Shalom" greeting via
  // the i18n default. Tenants can still set custom greeting copy in the
  // White-Label Editor. (Kept symmetric with brij.ts.)
};

export default m2;
